import React from "react";
import { useAppSelector, useAppDispatch } from "../../../../store";
import { addDroneSystem } from "../../../../store/features/optionalSystemsSlice";
import { DroneSystem } from "../../../../types/optionalSystems";
import {
  validateMiningDrone,
  validateProbeDrone,
  validateAdvancedProbeDrone,
  validateRepairDrone,
} from "../../../../utils/calculations/optionalSystems";
import { formatCurrency } from "../../../../utils/formatting";
import { selectors } from "../../../../store/selectors";

const DRONE_TYPES = [
  {
    id: "miningDrone",
    label: "Mining Drone",
    subtype: "mining",
    validate: validateMiningDrone,
    defaultQty: 5,
  },
  {
    id: "probeDrone",
    label: "Probe Drone",
    subtype: "probe",
    validate: validateProbeDrone,
    defaultQty: 5,
  },
  {
    id: "advancedProbeDrone",
    label: "Advanced Probe Drone",
    subtype: "advancedProbe",
    validate: validateAdvancedProbeDrone,
    defaultQty: 5,
  },
  {
    id: "repairDrone",
    label: "Repair Drone",
    subtype: "repair",
    validate: (_: number, shipTonnage: number) =>
      validateRepairDrone(shipTonnage),
    defaultQty: 1,
  },
];

interface DroneSystemsProps {
  shipTonnage: number;
  techLevel: number;
  tlOverrideActive?: boolean;
}

const DroneSystems: React.FC<DroneSystemsProps> = ({
  shipTonnage,
  techLevel,
  tlOverrideActive = false,
}) => {
  const dispatch = useAppDispatch();

  // State for each drone type's quantity (for non-repair drones)
  const [quantities, setQuantities] = React.useState<{ [id: string]: number }>(
    DRONE_TYPES.reduce(
      (acc, d) => {
        acc[d.id] = d.defaultQty;
        return acc;
      },
      {} as { [id: string]: number },
    ),
  );

  // Get calculated stats using selectors at the top level
  const miningDroneStats = useAppSelector((state) =>
    selectors.derived.selectMiningDroneStats(
      state,
      quantities.miningDrone || 5,
    ),
  );
  const probeDroneStats = useAppSelector((state) =>
    selectors.derived.selectProbeDroneStats(state, quantities.probeDrone || 5),
  );
  const advancedProbeDroneStats = useAppSelector((state) =>
    selectors.derived.selectAdvancedProbeDroneStats(
      state,
      quantities.advancedProbeDrone || 5,
    ),
  );
  const repairDroneStats = useAppSelector((state) =>
    selectors.derived.selectRepairDroneStats(state, shipTonnage),
  );

  const handleAddDrone = (droneType: (typeof DRONE_TYPES)[0]) => {
    const inputValue = quantities[droneType.id] || droneType.defaultQty;
    let stats;

    if (droneType.id === "miningDrone") {
      stats = miningDroneStats;
    } else if (droneType.id === "probeDrone") {
      stats = probeDroneStats;
    } else if (droneType.id === "advancedProbeDrone") {
      stats = advancedProbeDroneStats;
    } else if (droneType.id === "repairDrone") {
      stats = repairDroneStats;
    } else {
      return;
    }

    // Add multiple drones based on the quantity input
    for (let i = 0; i < inputValue; i++) {
      const system: DroneSystem = {
        id: `drone-${Date.now()}-${i}`,
        type: "drone",
        subtype: droneType.subtype as
          | "mining"
          | "probe"
          | "advancedProbe"
          | "repair",
        name: droneType.label,
        description: "",
        cost: stats.cost,
        tonnage: stats.tonnage,
        power: stats.power,
        techLevel: stats.techLevel,
        properties: {
          quantity: 1, // Each individual drone has quantity 1
        },
      };

      dispatch(addDroneSystem(system));
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Drone Systems</h2>
      <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-white dark:bg-gray-800">
        <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Available Drone Systems</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DRONE_TYPES.map((droneType) => {
            const qty = quantities[droneType.id];
            const errors = droneType.validate(qty, shipTonnage);

            // Get calculated stats based on drone type
            let stats;
            if (droneType.id === "miningDrone") {
              stats = miningDroneStats;
            } else if (droneType.id === "probeDrone") {
              stats = probeDroneStats;
            } else if (droneType.id === "advancedProbeDrone") {
              stats = advancedProbeDroneStats;
            } else if (droneType.id === "repairDrone") {
              stats = repairDroneStats;
            } else {
              stats = { cost: 0, tonnage: 0, power: 0, note: undefined };
            }

            return (
              <div
                key={droneType.id}
                className="border rounded-lg p-4 flex flex-col justify-between bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
              >
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    {droneType.label}
                  </div>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={qty}
                      onChange={(e) =>
                        setQuantities({
                          ...quantities,
                          [droneType.id]: Math.max(
                            1,
                            Math.floor(Number(e.target.value)),
                          ),
                        })
                      }
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-600 dark:text-gray-100"
                    />
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                    <div>
                      <b>Cost:</b> {formatCurrency(stats.cost)}
                    </div>
                    <div>
                      <b>Tonnage:</b> {stats.tonnage} tons
                    </div>
                  </div>
                  {errors.length > 0 && (
                    <div className="text-xs text-red-600 dark:text-red-400 mb-2">
                      {errors.map((e) => (
                        <div key={e}>{e}</div>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  className={`px-4 py-1 rounded text-white font-semibold ${errors.length > 0 ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                  onClick={() => handleAddDrone(droneType)}
                  disabled={errors.length > 0}
                >
                  Add Drone
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DroneSystems;
